import { useMemo, useState } from "react"
import {
    useReactTable,
    getCoreRowModel,
    createColumnHelper,
    flexRender
} from 'react-table'

import {useConservation, useOrbl} from "../hooks.js"
import {SimpleDictTable} from "../widgets/SimpleTable.jsx"
import {DecoratedSimpleDictTable} from "../widgets/SimpleTable.jsx"
import {SpinnerSvg} from "../widgets/Spinner.jsx"
import { downloadFile, HeaderWithModal } from "../utils.jsx"

const columnHelper = createColumnHelper()

const intervalsMaxLength = 28

const shortenIntervals = value => {
    const startLength = 14;
    const endLength = 11;
    return `${value.slice(0, startLength)}...${value.slice(-endLength)}`;
}

const ExpandableIntervals = ({value}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const shouldShorten = value && value.length > intervalsMaxLength;
    const displayValue = shouldShorten && !isExpanded ? shortenIntervals(value) : value;

    return shouldShorten ? (
        <button
            type="button"
            className="break-all text-left hover:underline"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
        >
            {displayValue}
        </button>
    ) : displayValue
}

const RoundedScore = ({value}) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return value
    }
    return <span title={value}>{Number(value).toFixed(3)}</span>
}

export default ({species_sn, protein_seq_id}) => {

    const {
        orthologs,
        paralogs
    } = useConservation({
        species_sn,
        protein_seq_id,
        renderSpeciesCell: species => species.sc
    })

    const {
        orblRows,
        isReady
    } = useOrbl({
        species_sn,
        protein_seq_id
    })


    const blastColumnTitles = {
        sseq_a: "subject",
        pident: "pident",
        length: "length",
        mismatch: "mismatch",
        gapopen: "gapopen",
        qstart: "qstart",
        qend: "qend",
        sstart: "sstart",
        send: "send",
        evalue: "evalue",
        bitscore: "bitscore",
        qcovs: "qcovs",
        qlen: "qlen",
        slen: "slen"
    }

const ColumnHeader = ({ label, description }) => (
    <div className="group relative inline-block cursor-default">
        <span>{label}</span>

        {description ? (
            <div
                className="
                    invisible absolute bottom-full right-0 z-50 mb-1
                    w-60 max-w-xs rounded bg-blue-400 p-2
                    text-left text-xs font-normal normal-case text-white
                    opacity-0 transition
                    group-hover:visible group-hover:opacity-100
                    whitespace-pre-line break-words
                "
            >
                {description}
            </div>
        ) : null}
    </div>
)

const orthologColumnDescriptions = {
    subject: "Id of the orthologous protein sequence in the other species.",
    pident: "Percentage of identical matches.",
    length: "Total alignment length.",
    mismatch: "Number of mismatches.",
    gapopen: "Number of gap openings.",
    qstart: "Start of the alignment in the query.",
    qend: "End of the alignment in the query.",
    sstart: "Start of the alignment in the subject.",
    send: "End of the alignment in the subject.",
    evalue: "E-value of the alignment.",
    bitscore: "Bit score of the alignment.",
    qcovs: "Query coverage per subject.",
    qlen: "Length of the query sequence.",
    slen: "Length of the subject sequence."
}

const paralogColumnDescriptions = {
    sseq_a: "Id of the paralogous protein sequence in the other species.",
    pident: "Percentage of identical matches.",
    length: "Total alignment length.",
    mismatch: "Number of mismatches.",
    gapopen: "Number of gap openings.",
    qstart: "Start of the alignment in the query.",
    qend: "End of the alignment in the query.",
    sstart: "Start of the alignment in the subject.",
    send: "End of the alignment in the subject.",
    evalue: "E-value of the alignment.",
    bitscore: "Bit score of the alignment.",
    qcovs: "Query coverage per subject.",
    qlen: "Length of the query sequence.",
    slen: "Length of the subject sequence.",
    is_isoform: "Indicates whether the paralogous sequence is an isoform of the original protein sequence."
}





    const paralogColumTitles = {
        ...blastColumnTitles,
        is_isoform: "isoform"
    }


    const orblColumnTitles = {
        Intervals: "Intervals",
        "ORF type": "ORF type",
        "Alignment set": "Alignment set",
        links: "links",
        ORBLv: "ORBLv",
        ATGRelBL: "ATGRelBL",
        StopRelBL: "StopRelBL",
        FrameRelBL: "FrameRelBL",
        ORBLq: "ORBLq",
        Warning: "Warning"
    }

const orblColumnDescriptions = {

  Intervals: "Genomic intervals overlapped by the ORF",
  "ORF type": "Absolute ORF type. 'mixed' type denotes ORF that has more than one ORF type accross transcripts.",
  "Alignment set": "Alignment set used to compute scores.",
  links: "CodAlignView and UCSCView links to visualize the selected alignment and the ORF intervals respectively.",
  "ORBLv": "Conservation measure. \n Possible values are between 0 and 1, with larger values indicating more conservation.",
  ATGRelBL: "Start codon conservation in the given clade.  \n Possible values are between 0 and 1, with larger values indicating more conservation.",
  StopRelBL: "Stop codon conservation in the given clade.  \n Possible values are between 0 and 1, with larger values indicating more conservation.",
  FrameRelBL: "Reading frame conservation in the given clade. \n Possible values are between 0 and 1, with larger values indicating more conservation.",
  ORBLq: "Evolutionary onstraint measure. \n Possible values are between 0 and 1, with larger values indicating more constraint.",
  Warning: "Known bias that could indicate potential false positives. \n Possible values are 'NoTranslationEvidence', 'OverlapsOtherStrandCoding' and 'OverlapsPseudo'. See documentation for more information.",
}
  
    const renderOrblCell = {
        Intervals: value => <ExpandableIntervals value={value} />,
        links: (_value, row) => (
            <div className="flex gap-2">
                {row.UCSCView && (
                    <a
                        href={row.UCSCView}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                    >
                        UCSCView
                    </a>
                )}
                {row.CodAlignView && (
                    <a
                        href={row.CodAlignView}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                        CodAlignView
                    </a>
                )}
            </div>
        ),
        ORBLv: value => <RoundedScore value={value} />,
        ATGRelBL: value => <RoundedScore value={value} />,
        StopRelBL: value => <RoundedScore value={value} />,
        FrameRelBL: value => <RoundedScore value={value} />,
        ORBLq: value => <RoundedScore value={value} />,
    }


    const columns= useMemo(() =>[
          columnHelper.accessor(
              row => row[0],
              {
                header: 'Species',
                cell: info => {
                    const o = info.getValue()
                    if(o === null) {
                        return null
                    }

                    return o.content
                }
              }
          ),
            ...["subject",
                "pident",
                "length",
                "mismatch",
                "gapopen",
                "qstart",
                "qend",
                "sstart",
                "send",
                "evalue",
                "bitscore",
                "qcovs",
                "qlen",
                "slen"
            ].map(
                  (title, idx) =>
                      columnHelper.accessor(
                          row => row[idx + 1],
                          {
                              id: title,
                              header: () => (
                                  <ColumnHeader
                                      label={title}
                                      description={orthologColumnDescriptions[title]}
                                  />
                              ),
                              cell: info => info.getValue().content
                          }
                      )
              )
        ],[])

    const orthologTable = useReactTable({
        data: orthologs,
        columns,
        getCoreRowModel: getCoreRowModel()
    })
    
    const downloadOrthologs = e => {
      
      e.preventDefault()

      let headers = ["species,subject,pident,length,mismatch,gapopen,qstart,qend,sstart,send,evalue,bitscore,qcovs,qlen,slen"]
    
      let rows = orthologs.reduce((acc, orth) => {
        const u = orth.map((x, idx)=> x !== null ? x.content : acc[acc.length-1][0])
        acc.push(u)
        return acc
      }, [])
    
      downloadFile({
        data: [...headers, ...rows.map(r => r.join(','))].join('\n'),
        fileName: `orthologs_${protein_seq_id}.csv`,
        fileType: 'text/csv',
      })
    }

    const downloadParalogs = e => {
      
      e.preventDefault()

      let headers = ["subject,pident,length,mismatch,gapopen,qstart,qend,sstart,send,evalue,bitscore,qcovs,qlen,slen,isoform"]
      const columns = ['sseq_a','pident','length','mismatch','gapopen','qstart','qend','sstart','send','evalue','bitscore','qcovs','qlen','slen','is_isoform']
      console.log(paralogs)
      console.log(paralogColumTitles)
      const rows = paralogs.map(p => columns.map(t => p[t]))
      downloadFile({
        data: [...headers, ...rows.map(r => r.join(','))].join('\n'),
        fileName: `paralogs_${protein_seq_id}.csv`,
        fileType: 'text/csv',
      })
    }

    const renderOrthologTable = () =>  <table className="w-full text-sm text-left text-gray-700 border-collapse">
        <thead>
          {orthologTable.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <th
                      key={header.id}
                      className="px-3 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 whitespace-nowrap"
                      colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {orthologTable.getRowModel().rows.map(row => {
            return (
              <tr
                  key={row.id}
              >
                    {
                        row.getVisibleCells().map(cell => {
                            const ctx = cell.getContext()
                            const v = ctx.getValue()
                            if(v === null) {
                                return null
                            }
                            if(!v) {
                                return null
                            }
                            return (
                                <td
                                    rowSpan={v.rowSpan}
                                    className="px-3 py-2 border-b border-slate-200"
                                    key={cell.id}
                                >
                                    {flexRender(cell.column.columnDef.cell, ctx)}
                                </td>
                            )
                        })
                    }
              </tr>
            )
          })}
        </tbody>
      </table>

    return <div className="shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] mt-3 mr-3 rounded p-3">
        <div className="border-b width-full border-slate-200">
          <HeaderWithModal title="Conservation Analysis">
            <p>These tables contain the results of a phylogenetic conservation analyses of the protein sequence. 
            <a className="cursor-pointer hover:underline text-blue-600" href='/documentation'> See the documentation</a> for more details on the method.</p>
            <p>ORBL (ORF Relative Branch Length) scores of the ORF are reported in the first table. ORBLv measures cross-species evolutionary conservation of "ORFness" — an aligned start codon, stop codon, and open reading frame in orthologous species — while ORBLq measures evolutionary constraint on ORFness relative to untranslated ORFs of the same biotype and length. The UCSCView and CodAlignView links open the interval in the UCSC Genome Browser and the color-coded alignment, respectively.</p>
          
          </HeaderWithModal>
        </div>
        <div className="relative w-full font-semibold mt-3">
          <HeaderWithModal title="ORBL scores">
            <p>Column description:</p>
            <table className="w-full text-sm text-left text-gray-700 border-collapse" >
              <tr>
                <th className="px-3 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 whitespace-nowrap">Column</th>
                <th className="px-3 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 whitespace-nowrap">Description</th>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">Intervals</td>
                <td className="px-3 py-2 border-b border-slate-200">Genomic intervals overlapped by the ORF</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">ORF type</td>
                <td className="px-3 py-2 border-b border-slate-200">Absolute ORF type. "mixed" type denotes ORF that has more than one ORF type accross transcripts.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">Alignment set</td>
                <td className="px-3 py-2 border-b border-slate-200">Alignment set used to compute scores.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">links</td>
                <td className="px-3 py-2 border-b border-slate-200">CodAlignView and UCSCView links to visualize the selected alignment and the ORF intervals respectively.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">ORBLv</td>
                <td className="px-3 py-2 border-b border-slate-200">Conservation measure. Possible values are between 0 and 1, with larger values indicating more conservation.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">ATGRelBL</td>
                <td className="px-3 py-2 border-b border-slate-200">Start codon conservation in the given clade. Possible values are between 0 and 1, with larger values indicating more conservation.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">StopRelBL</td>
                <td className="px-3 py-2 border-b border-slate-200">Stop codon conservation in the given clade. Possible values are between 0 and 1, with larger values indicating more conservation.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">FrameRelBL</td>
                <td className="px-3 py-2 border-b border-slate-200">Reading frame conservation in the given clade. Possible values are between 0 and 1, with larger values indicating more conservation.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">ORBLq</td>
                <td className="px-3 py-2 border-b border-slate-200">Evolutionary onstraint measure. Possible values are between 0 and 1, with larger values indicating more constraint.</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">Warning</td>
                <td className="px-3 py-2 border-b border-slate-200">Known bias that could indicate potential false positives. Possible values are "NoTranslationEvidence", "OverlapsOtherStrandCoding" and "OverlapsPseudo". See documentation for more information.</td>
              </tr>
            </table>
          </HeaderWithModal>
        </div>
        <p className="text-sm text-gray-600">ORF Relative Branch Length (ORBL) measures cross-species conservation of the ORF.{" "}
            <a className="cursor-pointer hover:underline text-blue-600" href="https://github.com/iljungr/ORBL_tools" target="_blank" rel="noreferrer">You can find more details about ORBL here</a>
        </p>
        {!isReady ? (
            <div className="flex items-center py-2">
                <SpinnerSvg svgClasses={"w-6 h-6"}/>
            </div>
        ) : (
            <DecoratedSimpleDictTable colDefs={orblColumnTitles} colDescriptions={orblColumnDescriptions} rows={orblRows} renderCell={renderOrblCell}/>
        )}
        <div className="border-b width-full border-slate-200 font-semibold mt-3">
          <HeaderWithModal title="Orthologs">
            <p>Column description:</p>
            <table className="w-full text-sm text-left text-gray-700 border-collapse" >
              <tr>
                <th className="px-3 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 whitespace-nowrap">Column</th>
                <th className="px-3 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 whitespace-nowrap">Description</th>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">Subject</td>
                <td className="px-3 py-2 border-b border-slate-200">Id of the Othologous protein sequence in the other species</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">pident</td>
                <td className="px-3 py-2 border-b border-slate-200">Percentage of identical matches</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">mismatch </td>
                <td className="px-3 py-2 border-b border-slate-200">Number of mismatches</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">gapopen </td>
                <td className="px-3 py-2 border-b border-slate-200">Number of gap openings</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qstart </td>
                <td className="px-3 py-2 border-b border-slate-200">Start of alignment in query</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qend </td>
                <td className="px-3 py-2 border-b border-slate-200">End of alignment in query</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">sstart </td>
                <td className="px-3 py-2 border-b border-slate-200">End of alignment in subject</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">send </td>
                <td className="px-3 py-2 border-b border-slate-200">Percentage of identical matches</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qcovs </td>
                <td className="px-3 py-2 border-b border-slate-200">Query Coverage Per Subject</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qlen </td>
                <td className="px-3 py-2 border-b border-slate-200">query length</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">slen </td>
                <td className="px-3 py-2 border-b border-slate-200">subject length</td>
              </tr>
            </table>
          </HeaderWithModal>
          <button className='right-0 top-0 absolute font-medium text-blue-600 dark:text-blue-500 hover:underline' type='button' onClick={downloadOrthologs}>download</button>
        {renderOrthologTable()}
        </div>
        <div className="border-b width-full border-slate-200 font-semibold mt-3">
          <HeaderWithModal title="Paralogs">
            <p>Column description:</p>
            <table className="w-full text-sm text-left text-gray-700 border-collapse" >
              <tr>
                <th className="px-3 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 whitespace-nowrap">Column</th>
                <th className="px-3 py-2 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-300 whitespace-nowrap">Description</th>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">Subject</td>
                <td className="px-3 py-2 border-b border-slate-200">Id of the Paralogous protein sequence in the other species</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">pident</td>
                <td className="px-3 py-2 border-b border-slate-200">Percentage of identical matches</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">mismatch </td>
                <td className="px-3 py-2 border-b border-slate-200">Number of mismatches</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">gapopen </td>
                <td className="px-3 py-2 border-b border-slate-200">Number of gap openings</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qstart </td>
                <td className="px-3 py-2 border-b border-slate-200">Start of alignment in query</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qend </td>
                <td className="px-3 py-2 border-b border-slate-200">End of alignment in query</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">sstart </td>
                <td className="px-3 py-2 border-b border-slate-200">End of alignment in subject</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">send </td>
                <td className="px-3 py-2 border-b border-slate-200">Percentage of identical matches</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qcovs </td>
                <td className="px-3 py-2 border-b border-slate-200">Query Coverage Per Subject</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">qlen </td>
                <td className="px-3 py-2 border-b border-slate-200">query length</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border-b border-slate-200">slen </td>
                <td className="px-3 py-2 border-b border-slate-200">subject length</td>
              </tr>
            </table>
          </HeaderWithModal>
          <button className='right-0 top-0 absolute font-medium text-blue-600 dark:text-blue-500 hover:underline' type='button' onClick={downloadParalogs}>download</button>
        </div>
        <DecoratedSimpleDictTable colDefs={paralogColumTitles} colDescriptions={paralogColumnDescriptions} rows={paralogs}/>
    </div>
}

